/*
 * Copyright (c) CIRROM 2026.
 */

import { useEffect, useState } from "react";
import { getAccountSummary } from "../../../api/accounts";
import { AppliedChange } from "../../../api/suggestedchanges";

/*
 * A company written to the CRM that Cirrom's copy has not read back yet.
 *
 * Cirrom's copy of a contact is only refreshed by the next batch's read of
 * the CRM, so after a company write the record still names the old employer -
 * which looked like the write had failed, and kept offering the old account's
 * addresses. Updating the stored copy on write is not safe while a batch may
 * still be analysing it, so the correction lives here instead: the surface
 * shows what the CRM now holds, marked as not yet synced, and the stored copy
 * is left alone until the read that owns it.
 */
export interface UnsyncedCompany {
  /** The account the CRM now links the contact to. */
  accountId: string;
  /** Its display name, once known; null while it is still being looked up. */
  name: string | null;
  writtenBy: string | null;
  writtenAt: string;
}

/**
 * The applied company change the snapshot has not caught up with, if any.
 *
 * Only the latest company change counts - an older one has been superseded -
 * and only while no batch has read the CRM since it was written: a change
 * from the snapshot's own batch was written after that batch's read. Once a
 * newer batch has read the CRM its values are trusted as they stand, which
 * also covers the company being changed again in the CRM directly.
 */
export const findUnsyncedCompanyChange = (
  appliedChanges: AppliedChange[] | null,
  snapshot: { batchId?: number | null; crmAccountId?: string | null }
): AppliedChange | undefined => {
  if (!appliedChanges || !snapshot.batchId) return undefined;
  // Newest first, so the first company change is the one in force.
  const latest = appliedChanges.find((change) => change.field === "company");
  if (
    !latest ||
    latest.change_status !== "WRITTEN" ||
    !latest.new_system_id ||
    latest.batch_id !== snapshot.batchId ||
    String(latest.new_system_id) === String(snapshot.crmAccountId ?? "")
  ) {
    return undefined;
  }
  return latest;
};

/**
 * The applied change to any other field the snapshot has not caught up with,
 * if any. The same rule as findUnsyncedCompanyChange: only the latest change
 * to the field counts, only once written, and only while no batch has read
 * the CRM since - compared on the value, as these fields have no linked id.
 */
export const findUnsyncedFieldChange = (
  appliedChanges: AppliedChange[] | null,
  snapshot: { batchId?: number | null },
  fieldName: string,
  storedValue: string | null
): AppliedChange | undefined => {
  if (!appliedChanges || !snapshot.batchId) return undefined;
  // Newest first, so the first change to the field is the one in force.
  const latest = appliedChanges.find((change) => change.field === fieldName);
  if (
    !latest ||
    latest.change_status !== "WRITTEN" ||
    latest.batch_id !== snapshot.batchId ||
    (latest.new_value ?? "") === (storedValue ?? "")
  ) {
    return undefined;
  }
  return latest;
};

export const useUnsyncedCompany = (args: {
  integratedSystemId?: number | null;
  appliedChanges: AppliedChange[] | null;
  snapshotBatchId?: number | null;
  crmAccountId?: string | null;
}): UnsyncedCompany | null => {
  const change = findUnsyncedCompanyChange(args.appliedChanges, {
    batchId: args.snapshotBatchId,
    crmAccountId: args.crmAccountId,
  });
  const accountId = change?.new_system_id ?? null;
  /*
   * A Monitor or picker write stores the account id as its value; a Cirrom
   * suggestion stores the name. Only the first needs a lookup.
   */
  const storedName =
    change?.new_value && change.new_value !== accountId
      ? change.new_value
      : null;

  const [lookedUpName, setLookedUpName] = useState<{
    accountId: string;
    name: string | null;
  } | null>(null);

  useEffect(() => {
    if (!accountId || storedName || !args.integratedSystemId) return undefined;
    let cancelled = false;
    getAccountSummary(args.integratedSystemId, accountId)
      .then((summary) => {
        if (!cancelled) {
          setLookedUpName({ accountId, name: summary?.crm?.company ?? null });
        }
      })
      .catch(() => {
        // The marker still stands without a name; it just says less.
        if (!cancelled) setLookedUpName({ accountId, name: null });
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, storedName, args.integratedSystemId]);

  if (!change || !accountId) return null;
  return {
    accountId,
    name:
      storedName ??
      (lookedUpName?.accountId === accountId ? lookedUpName.name : null),
    writtenBy: change.user,
    writtenAt: change.last_update_time,
  };
};
